import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export namespace OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration {
    export interface Response {
        isAvailable: boolean;
        topCountries: TopCountries;
        topDurationUsers: TopDurationUsers;
        chart: Chart;
        total: Total;
        hasStats: boolean;
    }

    export interface Chart {
        visitors: Duration[];
        duration: Duration[];
    }

    export interface Duration {
        date: Date;
        count: number;
    }

    export interface TopCountries {
        hasMore: boolean;
        totals: Totals;
        rows: Row[];
    }

    export interface Row {
        rank: number;
        countryName: string;
        countryCode: string;
        viewsCount: ViewsCount;
    }

    export interface ViewsCount {
        total: number;
        guests: number;
        users: number;
        subscribers: number;
    }

    export interface Totals {
        total: number;
        guests: string;
        users: string;
        subscribers: number;
    }

    export interface TopDurationUsers {
        totals: Totals;
    }

    export interface Total {
        current: string;
        delta: number;
    }
}

type ProfileVisitorsAllQuery = {
    mode?: 'all' | 'range' | 'since';
    startDate?: Date;
    endDate?: Date;
    sinceDate?: Date;
};

const useSubscriberStatsProfileVisitorsAll = (subscriberId: string | undefined, query?: ProfileVisitorsAllQuery) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const axios = useAxios();

    const mode = query?.mode || 'all';

    const fetchData = async (): Promise<OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response | null> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !subscriberId) {
            return null;
        }

        const search = new URLSearchParams();
        search.set('mode', mode);
        if (query?.startDate) {
            search.set('startDate', query.startDate.toISOString());
        }
        if (query?.endDate) {
            search.set('endDate', query.endDate.toISOString());
        }
        if (query?.sinceDate) {
            search.set('sinceDate', query.sinceDate.toISOString());
        }
        const queryUrl = `${settingsContext.routes.onlyFans.base}${subscriberId}/stats/profile/visitors/all?${search.toString()}`;

        return axios
            .get(queryUrl)
            .then(response => response.data as OnlyFansStatisticsProfileVisitorsWithCountriesAndDuration.Response)
            .catch(error => {
                handleHttpError(error, dialog);

                return null;
            });
    };

    return useQuery(
        ['subscriberStatsProfileVisitorsAll', subscriberId, mode, query?.startDate, query?.endDate, query?.sinceDate],
        fetchData,
        {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 5, // 5 minutes
            enabled: !!(userContext.jwtToken && settingsContext.apiKey && subscriberId),
        },
    );
};

export default useSubscriberStatsProfileVisitorsAll;
